import AdminFormLayout, { FormSection } from "../../components/common/form-layout/AdminFormLayout";
import { useEffect, useRef, useState } from "react";
import { postApi } from "../../services/api/globalApi";
import { Formik, Form } from "formik";
import Input from "../../components/common/inputs/Input";
import Button from "../../components/common/button/Button";
import {
  errorMessage,
  successMessage,
  warningMessage,
} from "../../utils/toastify-message/ToastifyMessage";
import ImagesModal from "../../components/common/modals/imgaes-modal/ImagesModal";
import { buildPublicFileUrl } from "../../services/config";
import { useLocation, useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { utilsState } from "../../sotre/redux/features/utilsSlice";
import CategoryPartType from "../../components/common/inputs/CategoryPartTypeSelect";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FaGripVertical } from "react-icons/fa";

// Existing sliders may have arbitrary priority numbers. Keep their current
// display order until the administrator chooses a new one.
const orderSlides = (items) =>
  [...items].sort((a, b) => {
    const first = Number(a.priority);
    const second = Number(b.priority);
    return (Number.isFinite(first) ? first : 0) - (Number.isFinite(second) ? second : 0);
  });

const validTextSize = (value) => value === "" || value === undefined || value === null ||
  (Number.isInteger(Number(value)) && Number(value) >= 12 && Number(value) <= 72);
const optionalTextSize = (value) => value === "" || value === undefined || value === null ? undefined : Number(value);

function SortableSlide({ slide, index, onDelete, onSlideChange }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: slide.sortId });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex flex-wrap items-center gap-3 rounded-xl border bg-white p-3 shadow-sm dark:bg-gray-800 dark:border-gray-700 ${
        isDragging ? "relative z-10 border-blue-500 shadow-lg" : "border-gray-200"
      }`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`جابه‌جایی اسلاید ${index + 1}`}
        title="برای جابه‌جایی بکشید؛ با صفحه‌کلید نیز قابل استفاده است"
        className="flex h-11 w-9 shrink-0 touch-none items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:hover:bg-gray-700"
      >
        <FaGripVertical aria-hidden="true" />
      </button>
      <span className="w-6 shrink-0 text-center text-sm font-bold text-blue-700 dark:text-blue-300">
        {index + 1}
      </span>
      <img
        src={buildPublicFileUrl(slide.image, { variant: "thumb_" })}
        onError={(event) => {
          event.currentTarget.onerror = null;
          event.currentTarget.src = buildPublicFileUrl(slide.image);
        }}
        alt={slide.name || `اسلاید ${index + 1}`}
        className="h-14 w-20 shrink-0 rounded-lg object-cover"
      />
      <div className="order-last w-full min-w-0 text-sm text-gray-700 dark:text-gray-200 sm:order-none sm:w-auto sm:flex-1">
        <label className="block text-xs">متن اسلاید
          <input
            type="text"
            value={slide.name ?? ""}
            onChange={(event) => onSlideChange(slide.sortId, "name", event.target.value)}
            aria-label={`متن اسلاید ${index + 1}`}
            placeholder="متن یا زیرنویس اسلاید"
            className="admin-field mt-1 block w-full"
          />
        </label>
        {slide.link && <p className="truncate text-xs text-gray-500 dark:text-gray-400" dir="ltr">{slide.link}</p>}
        <div className="mt-2 flex flex-wrap gap-2">
          <label className="text-xs">اندازه متن موبایل (px)
            <input type="number" min="12" max="72" value={slide.textSizeMobile ?? ""}
              onChange={(event) => onSlideChange(slide.sortId, "textSizeMobile", event.target.value)}
              placeholder="پیش‌فرض: ۲۰" className="admin-field mt-1 block w-32" />
          </label>
          <label className="text-xs">اندازه متن بزرگ‌تر (px)
            <input type="number" min="12" max="72" value={slide.textSizeDesktop ?? ""}
              onChange={(event) => onSlideChange(slide.sortId, "textSizeDesktop", event.target.value)}
              placeholder="پیش‌فرض: ۳۰" className="admin-field mt-1 block w-32" />
          </label>
        </div>
      </div>
      <button
        type="button"
        onClick={() => onDelete(slide.sortId)}
        className="ms-auto shrink-0 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 dark:text-red-400 dark:hover:bg-red-900/20 sm:ms-0"
        aria-label={`حذف اسلاید ${index + 1}`}
      >
        حذف
      </button>
    </li>
  );
}

export default function Slider() {
  const [slides, setSlides] = useState([]);
  const [saving, setSaving] = useState(false);
  const slideId = useRef(0);
  const makeSortable = (item) => ({
    ...item,
    sortId: item._id || `new-slide-${++slideId.current}`,
  });
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const [name, setName] = useState(null);
  const [part, setPart] = useState(null);
  const [position, setPosition] = useState(null);
  const imagesModal = useRef();
  const [image, setImage] = useState("");
  const [categoryPartTypes, setCategoryPartType] = useState([]);

  const { utils } = useSelector(utilsState);

  const formHandler = (values, { resetForm }) => {
    if (!validTextSize(values.textSizeMobile) || !validTextSize(values.textSizeDesktop)) {
      warningMessage("اندازه متن باید عددی بین ۱۲ تا ۷۲ پیکسل باشد.");
      return;
    }
    const newValues = makeSortable({
      ...values,
      image,
      textSizeMobile: optionalTextSize(values.textSizeMobile),
      textSizeDesktop: optionalTextSize(values.textSizeDesktop),
    });
    if (image) {
      setSlides((prev) => {
        if (!Array.isArray(prev)) return [newValues];
        return [newValues, ...prev];
      });
      setImage(null);
      resetForm();
    } else {
      warningMessage("لطفا برای اسلایدر عکس انتخاب کنید");
    }
  };

  const deleteSlide = (id) => setSlides((current) => current.filter((slide) => slide.sortId !== id));
  const changeSlideField = (id, field, value) => setSlides((current) => current.map((slide) =>
    slide.sortId === id ? { ...slide, [field]: value } : slide
  ));

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setSlides((current) => {
      const from = current.findIndex((slide) => slide.sortId === active.id);
      const to = current.findIndex((slide) => slide.sortId === over.id);
      return from < 0 || to < 0 ? current : arrayMove(current, from, to);
    });
  };

  const selectedImages = (item) => {
    if (item) {
      setImage(item.name);
      successMessage("عکس اضافه شد.");
    }
  };

  const navigate = useNavigate();

  const handleSlider = async () => {
    if (saving) return;
    if (slides.some((slide) => !validTextSize(slide.textSizeMobile) || !validTextSize(slide.textSizeDesktop))) {
      warningMessage("اندازه متن اسلایدها باید عددی بین ۱۲ تا ۷۲ پیکسل باشد.");
      return;
    }
    if (name?.trim() && slides?.length) {
      const path = "admin/sliders";
      const body = {
        categoryPartTypes,
        name,
        part,
        position,
        slides: slides.map(({ sortId, ...slide }, index) => ({
          ...slide,
          textSizeMobile: optionalTextSize(slide.textSizeMobile),
          textSizeDesktop: optionalTextSize(slide.textSizeDesktop),
          priority: index + 1,
        })),
      };
      try {
        setSaving(true);
        await postApi({ path, body });
        successMessage("اسلایدر با موفقیت ویرایش شد.");
        navigate("/slider-list");
      } catch (error) {
        console.log(error);
        errorMessage(error?.response?.data?.errors?.[0]?.message || "ذخیره ترتیب اسلایدها انجام نشد. دوباره تلاش کنید.");
      } finally {
        setSaving(false);
      }
    } else {
      warningMessage("لطفا مقادیر ورودی را کنترل کنید.");
    }
  };

  const location = useLocation();

  useEffect(() => {
    if (location?.state?.name) {
      setName(location?.state?.name);
      setPosition(location?.state?.position);
      setPart(location?.state?.part);
      setSlides(orderSlides(location?.state?.slides || []).map(makeSortable));
    }
  }, [location?.state]);

  useEffect(() => {
    if (location.pathname === "/add-slider") {
      reset();
    }
  }, [location.pathname]);

  const reset = () => {
    setName("");
    setPosition("");
    setPart("");
    setSlides([]);
  };
  const categoryPartTypesRef = useRef();

  return (
    <AdminFormLayout title={"مدیریت اسلایدر"} description="مشخصات اسلایدر و اسلایدها را تنظیم کنید و در پایان تغییرات را ذخیره کنید.">

      <FormSection title="مشخصات اسلایدر">
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div className="w-full">
            <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
              نام
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
              بخش
            </label>
            <select
              value={part}
              onChange={(e) => setPart(e.target.value)}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            >
              <option value={""}>انتخاب بخش</option>
              {utils?.SLIDER_PART_VALUES?.length
                ? utils?.SLIDER_PART_VALUES?.map((item) => {
                    return <option value={item.value}>{item.name}</option>;
                  })
                : null}
            </select>
          </div>
          <CategoryPartType
            label={"دسته بندی بخش"}
            oldData={location?.state?.categoryPartTypes}
            ref={categoryPartTypesRef}
            placeholder="انتخاب"
            onChange={setCategoryPartType}
          />
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
              موقعیت
            </label>
            <select
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            >
              <option value={""}>انتخاب موقعیت</option>
              {utils?.SLIDER_POSITION_VALUES?.length
                ? utils?.SLIDER_POSITION_VALUES?.map((item) => {
                    return <option value={item.value}>{item.name}</option>;
                  })
                : null}
            </select>
          </div>
        </div>
      </FormSection>
      <FormSection title="اسلایدها و ترتیب نمایش">

        <div className="mt-4">
          <Formik
            enableReinitialize
            initialValues={{
              name: "",
              link: "",
              textSizeMobile: "",
              textSizeDesktop: "",
            }}
            onSubmit={formHandler}
          >
            <Form>
              <div className="grid gap-6 mb-4 md:grid-cols-2">
              <Input
                  name="name"
                  placeholder="زیر نویس اسلاید"
                  label="زیر نویس اسلاید"
                  type="text"
                />
                <Input
                  name="link"
                  placeholder="لینک"
                  label="لینک"
                  type="text"
                />
                <Input name="textSizeMobile" type="number" min="12" max="72"
                  label="اندازه متن موبایل (پیکسل)" placeholder="خالی: اندازه فعلی ۲۰" />
                <Input name="textSizeDesktop" type="number" min="12" max="72"
                  label="اندازه متن نمایشگر بزرگ‌تر (پیکسل)" placeholder="خالی: اندازه فعلی ۳۰" />
                <div className="">
                  <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                    عکس
                  </label>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                      {image ? (
                        <img
                          src={buildPublicFileUrl(image, { variant: "thumb_" })}
                          onError={(event) => {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = buildPublicFileUrl(image);
                          }}
                          className="w-20 h-20 shrink-0 rounded-xl border border-gray-200 object-contain bg-gray-50 dark:border-gray-700 dark:bg-gray-800"
                          alt=""
                        />
                      ) : (
                        <p className="text-sm">هنوز تصویری انتخاب نشده است.</p>
                      )}
                      {/* <p className="max-w-12 overflow-hidden">{image || ""}</p> */}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                      {image && (
                        <Button
                          type="button"
                          onClick={() => setImage("")}
                          text={"حذف"}
                          color={"danger"}
                        />
                      )}

                      <Button
                        type="button"
                        onClick={() => imagesModal.current.showModal()}
                        text={image ? "ویرایش" : "انتخاب"}
                        color={"success"}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-y-2">
                <div className="admin-form-actions">
                  <Button type="submit" text={"افزودن اسلاید"} color={"primary"} />
                </div>
              </div>
            </Form>
          </Formik>
        </div>
        <div className="mt-6">
          <p className="mb-3 text-sm text-gray-600 dark:text-gray-300">
            اسلایدها را با کشیدن دستگیره جابه‌جا کنید. اسلاید بالاتر زودتر نمایش داده می‌شود؛ سپس تغییرات را ذخیره کنید.
          </p>
          {slides?.length ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={slides.map((slide) => slide.sortId)} strategy={verticalListSortingStrategy}>
                <ol className="space-y-2" aria-label="ترتیب نمایش اسلایدها">
                  {slides.map((slide, index) => (
                    <SortableSlide key={slide.sortId} slide={slide} index={index} onDelete={deleteSlide} onSlideChange={changeSlideField} />
                  ))}
                </ol>
              </SortableContext>
            </DndContext>
          ) : (
            <div className="w-full flex items-center justify-center my-3 py-2">
              <p>هیچ اسلایدی هنوز ساخته نشده است.</p>
            </div>
          )}
        </div>
      </FormSection>
      <div className="flex flex-col gap-y-2">
        <div className="admin-form-actions">
          <button
            onClick={handleSlider}
            disabled={saving}
            className="admin-button rounded-lg bg-emerald-700 px-6 py-2.5 text-sm text-white hover:bg-emerald-800 disabled:cursor-wait disabled:opacity-60"
            type="button"
          >
            {saving ? "در حال ذخیره..." : location?.state ? "ذخیره تغییرات اسلایدر" : "افزودن اسلایدر"}
          </button>
          <button type="button" className="admin-form-cancel" onClick={() => navigate("/slider-list")}>انصراف</button>
        </div>
      </div>
      <ImagesModal selectedImages={selectedImages} ref={imagesModal} />
    </AdminFormLayout>
  );
}
